import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  tap,
  map,
  filter,
  combineLatest,
  switchMapTo,
} from "rxjs";
import { environment } from "src/environments/environment";
import { toStringList } from "../interface/filters";
import { QuestionDetail } from "../modules/pages/analysis/models/question-detail";
import { ApplicationService } from "./application.service";
import { StoredData } from "./models/stored-data";
import { MyUtils } from "./my-utils.service";

export const QuestionTab = {
  QUESTION: "QUESTION",
  DASHBOARD: "DASHBOARD",
  KEY_QUESTIONS_PERFORMANCE: "KEY-QUESTIONS-PERFORMANCE",
  HOME_OSAT: "HOME_OSAT",
  HOME_OVERVIEW: "HOME_OVERVIEW",
  INTERVIEWS_SUMMARY: "INTERVIEWS-SUMMARY",
  CLOSED_LOOP: "CLOSED-LOOP",
} as const;

export type Context = (typeof QuestionTab)[keyof typeof QuestionTab];

@Injectable({
  providedIn: "root",
})
export class QuestionsService {
  url = environment.csibefrontend;

  change$: Observable<[MyUtils["settingsPayloadValue"], ApplicationService["navBarSelectValue"]]>;

  private latestConfig = new Map<
    Context,
    {
      brands: string[];
      region: string;
      section: string;
      startValidationDate: string;
      endValidationDate: string;
    }
  >([
    ["QUESTION", { brands: [], region: "", section: "", endValidationDate: "", startValidationDate: "" }],
    ["DASHBOARD", { brands: [], region: "", section: "", endValidationDate: "", startValidationDate: "" }],
  ]);

  private questionsListSubj = new BehaviorSubject<StoredData<QuestionDetail[]>>({
    state: "init",
    data: undefined,
    loader: false,
  });

  private overviewQuestionSubj: BehaviorSubject<StoredData<QuestionDetail>> = new BehaviorSubject<
    StoredData<QuestionDetail>
  >({ data: undefined, state: "init", loader: false });
  public overviewQuestion$ = this.overviewQuestionSubj.asObservable();

  private performanceQuestionsListSubj: BehaviorSubject<StoredData<QuestionDetail[]>> =
    new BehaviorSubject<StoredData<QuestionDetail[]>>({
      data: undefined,
      state: "init",
      loader: false,
    });
  public performanceQuestionsList$ = this.performanceQuestionsListSubj.asObservable();

  public questionSelectSubject: BehaviorSubject<QuestionDetail>;
  public questionSelect: Observable<QuestionDetail>;

  constructor(
    private http: HttpClient,
    private utils: MyUtils,
    private applicationService: ApplicationService,
  ) {
    const storedQuestion = localStorage.getItem("question");
    this.questionSelectSubject = new BehaviorSubject<QuestionDetail>(
      storedQuestion ? JSON.parse(storedQuestion) : {} as QuestionDetail
    );
    this.questionSelect = this.questionSelectSubject.asObservable();

    this.change$ = combineLatest([
      this.utils.settingsPayloadSelect,
      this.applicationService.navBarSelect.pipe(filter((navbar) => navbar && !!navbar.idItem)),
    ]).pipe(
      filter(
        ([settings, navbar]) =>
          !!settings.payload &&
          settings.state === "valid" &&
          settings.payload.question?.section === navbar.idItem
      )
    );

    this.subscribeToLanguage();
  }

  subscribeToLanguage() {
    this.applicationService.languageSelect.subscribe(() => {
      this.refreshListQuestionDetails();
    });
  }

  payloadHasSignificantUpdates(context: Context) {
    let latestConfig = this.latestConfig.get(context);
    let brandsAreDifferent = false,
      regionIsDifferent = false,
      sectionIsDifferent = false,
      periodIsDifferent = false;
    if (latestConfig) {
      brandsAreDifferent =
        this.utils.settingsPayloadValue.payload.network.brand.length !==
        latestConfig.brands.length ||
        this.utils.settingsPayloadValue.payload.network.brand.some(
          (brand) => !latestConfig!.brands.includes(brand)
        );
      regionIsDifferent =
        this.utils.settingsPayloadValue.payload.network.regions[0].region !== latestConfig.region;

      sectionIsDifferent =
        this.applicationService.navBarSelectValue.idItem !== latestConfig.section;

      periodIsDifferent = this.checkIfPeriodIsDifferent(latestConfig);
    }
    return brandsAreDifferent || regionIsDifferent || sectionIsDifferent || periodIsDifferent;
  }

  private checkIfPeriodIsDifferent(latestConfig: any): boolean {
    return this.utils.settingsPayloadValue.payload.period.current.to !== latestConfig.endValidationDate ||
      this.utils.settingsPayloadValue.payload.period.current.from !== latestConfig.startValidationDate;
  }

  getSectionType = (route: string) => {
    switch (route) {
      case "/interviewSummary/closed-loop":
        return "CLOSED-LOOP";
      case "/interviewSummary/interview-summary-elastic":
        return "INTERVIEWS-SUMMARY";
      case "/interviewSummary/interview-summary":
        return "INTERVIEWS-SUMMARY";
      default:
        return "QUESTION";
    }
  };

  refreshListQuestionDetails() {
    const routeValue = this.applicationService.previousCurrentRouteSubject.getValue();
    let route = routeValue?.current || "";
    const region = this.utils.settingsPayloadValue?.payload?.network?.regions?.[0]?.region ?? '';

    return this.getListQuestionDetails(
      this.utils.settingsPayloadValue.payload.network.brand,
      region,
      this.getSectionType(route),
      this.applicationService.navBarSelectValue?.idItem || ""
    );
  }

  getListQuestionDetails(brands: string[], region: string, tab: Context, section: string) {
    let params = new HttpParams();

    if (brands.length > 0) params = params.set("brand", brands.join() ?? '');
    if (region) params = params.set("region", region);
    if (tab) params = params.set("tab", tab);
    params = params.set("section", section ?? "");

    let endValidationDate = this.utils.settingsPayloadValue.payload.period.current.to;
    let startValidationDate = this.utils.settingsPayloadValue.payload.period.current.from;
    params = params.set("endvalidationdate", endValidationDate);
    params = params.set("locale", this.applicationService.languageSelectValue);

    const vehicle = this.utils.settingsPayloadValue.payload.vehicle;
    // These are multi-select, so send them comma-joined like the brand param above.
    const vehicleModels = toStringList(vehicle?.model);
    const vehicleModelTypes = toStringList(vehicle?.modelType);
    const vehicleEngines = toStringList(vehicle?.engine);
    if (vehicleModels.length > 0) params = params.set("vehicleModel", vehicleModels.join());
    if (vehicleModelTypes.length > 0) params = params.set("vehicleModelType", vehicleModelTypes.join());
    if (vehicleEngines.length > 0) params = params.set("vehicleEngine", vehicleEngines.join());

    console.log('[QuestionsService] getListQuestionDetails called with params:', {
      brands,
      region,
      tab,
      section,
      locale: this.applicationService.languageSelectValue,
      endValidationDate,
      vehicle
    });
    let endPoint = this.url + `v3/getListQuestionAll`;
    if (environment.featureFlags.ssoHarmonization) {
      endPoint = this.url + `login/v2/login/getListQuestionAll`;
    }
    return this.http.get<QuestionDetail[]>(endPoint, { params })
      .pipe(
        tap(res => {
          console.log('[QuestionsService] getListQuestionAll response:', res);
          this.latestConfig.set(tab, {
            brands: brands,
            region: region,
            section: section,
            endValidationDate: endValidationDate,
            startValidationDate: startValidationDate
          });
          res.forEach((question) => {
            if (question.hasOwnProperty('textId') && question.hasOwnProperty('textBody') && question.textId !== undefined) {
              this.utils.remoteQuestions[question.textId] = question.textBody;
            }
          });
        }),
        map(questionList => {
          const filteredList: QuestionDetail[] = [];
          const questions: string[] = [...new Set(questionList
            .filter(question => question.textId?.includes(':title') ?? false)
            .map(x => x.textId ?? ''))];

          questions.forEach(question => {
            const detail = questionList.find(x => x.textId === question);
            if (detail) filteredList.push(detail);
          });
          console.log('[QuestionsService] Filtered question list:', filteredList);
          return filteredList;
        })
      );
  }

  getOverviewQuestion(brands: string[], region: string, section: string) {
    if (this.payloadHasSignificantUpdates("DASHBOARD")) {
      this.setOverviewQuestion("waiting");
      return this.getListQuestionDetails(brands, region, "DASHBOARD", section).pipe(
        map((questions) => questions[0]),
        tap((question) => this.setOverviewQuestion("completed", question)),
        catchError((err) => {
          console.error(err);
          this.setOverviewQuestion("error");
          return of(undefined);
        }),
        switchMapTo(this.overviewQuestionSubj.asObservable())
      );
    } else {
      return this.overviewQuestionSubj.asObservable();
    }
  }

  private setOverviewQuestion(
    state: StoredData<QuestionDetail>["state"],
    data?: QuestionDetail | undefined
  ) {
    let newOverviewQuestionValue: StoredData<QuestionDetail> = {
      state: state,
      data: data,
      loader: state === "waiting",
    };
    this.overviewQuestionSubj.next(newOverviewQuestionValue);
  }

  private setQuestionList(
    state: StoredData<QuestionDetail[]>["state"],
    data?: QuestionDetail[] | undefined
  ) {
    let questionList: StoredData<QuestionDetail[]> = {
      state: state,
      data: data,
      loader: state === "waiting",
    };
    this.questionsListSubj.next(questionList);
  }

  setPerformanceQuestions(
    state: StoredData<QuestionDetail[]>["state"],
    data?: StoredData<QuestionDetail[]>["data"]
  ) {
    let performanceQuestionList: StoredData<QuestionDetail[]> = {
      state: state,
      data: data,
      loader: state === "waiting",
    };
    this.performanceQuestionsListSubj.next(performanceQuestionList);
  }

  savePerformanceQuestionsPreference(userAccount: string, questions: QuestionDetail[]) {
    let questionCodes = questions.map((q) => q.questionCode);
    let section = this.applicationService.navBarSelectValue?.idItem || "";
    let key = `${userAccount}_${section}_performanceQuestionPreferences`;
    let stringifiedCodes = JSON.stringify(questionCodes);
    localStorage.setItem(key, stringifiedCodes);
  }

  getPerformanceQuestionsPreferences(userAccount: string) {
    let section = this.applicationService.navBarSelectValue?.idItem || "";
    let key = `${userAccount}_${section}_performanceQuestionPreferences`;
    let savedData = localStorage.getItem(key);
    let parsedData: string[] | undefined = undefined;
    if (savedData) {
      try {
        parsedData = JSON.parse(savedData);
      } catch (e) {
        console.error("Error parsing performance questions preferences", e);
      }
    }
    return parsedData;
  }

  getQuestionsList(brands: string[], region: string, section: string) {
    if (this.payloadHasSignificantUpdates("QUESTION")) {
      this.setQuestionList("waiting");
      return this.getListQuestionDetails(brands, region, "QUESTION", section).pipe(
        tap((questions) => {
          this.setQuestionList("completed", questions);
        }),
        switchMapTo(this.questionsListSubj.asObservable())
      );
    } else {
      return this.questionsListSubj.asObservable();
    }
  }

  public setQuestionValue(question: QuestionDetail) {
    localStorage.setItem("question", JSON.stringify(question));
    this.questionSelectSubject.next(question);
  }

  public get quesitonSelectValue() {
    return this.questionSelectSubject.value;
  }
}
